import React from "react";
import { Box, Paper, Typography, Chip, Avatar } from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { getOrderStatusColor } from "../../utils/Constant";
import Loading from "../loading/loading";

interface SearchResultsProps {
  loading: boolean;
  searchResults: any;
  searchValue: string;
  onOrderClick: (order: any) => void;
}

const formatAddress = (order: any) => {
  const address1 = order?.propertyRecord?.address1 || "";
  const address2 = order?.propertyRecord?.address2 || "";
  const city = order?.propertyRecord?.city || "";
  const state = order?.propertyRecord?.state || "";
  const zipCode = order?.propertyRecord?.zipCode || "";

  const parts = [address1, address2, city, state, zipCode].filter(
    (part) => part && part.trim()
  );
  return parts.join(", ");
};

const getSearchResultsData = (searchResults: any) => {
  if (searchResults?.items) {
    return searchResults.items;
  } else if (searchResults && !searchResults.items) {
    return Array.isArray(searchResults) ? searchResults : [];
  }
  return [];
};

export default function SearchResults({
  loading,
  searchResults,
  searchValue,
  onOrderClick,
}: SearchResultsProps) {
  const searchResultsData = getSearchResultsData(searchResults);
  const showSearchResults =
    searchValue.trim() && (loading || searchResults !== null);

  if (!showSearchResults) {
    return null;
  }

  return (
    <Paper
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        width: 350,
        maxHeight: 500,
        overflow: "auto",
        mt: 0.5,
        zIndex: 1300,
        boxShadow: 3,
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {loading ? (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Loading />
        </Box>
      ) : searchResultsData.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {searchResultsData.map((order: any) => (
            <Box
              key={order.id}
              sx={{
                borderBottom: "1px solid",
                borderColor: "#E0E0E0",
                cursor: "pointer",
                transition: "background-color 0.2s",
                "&:hover": {
                  background: "var(--primary-selected, #E35F0014)",
                },
              }}
              onMouseDown={() => onOrderClick(order)}
            >
              <Box sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 12,
                        color: "#000000",
                        opacity: 0.6,
                        minWidth: 80,
                      }}
                    >
                      Order #
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 16,
                        color: "#000000",
                        opacity: 0.87,
                        textAlign: "left",
                        flex: 1,
                      }}
                    >
                      {order.orderNumber || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 12,
                        color: "#000000",
                        opacity: 0.6,
                        minWidth: 80,
                      }}
                    >
                      Loan #
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 16,
                        color: "#000000",
                        opacity: 0.87,
                        textAlign: "left",
                        flex: 1,
                      }}
                    >
                      {order?.loanNumber || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 80,
                      }}
                    >
                      {order?.requester?.profilePicture && (
                        <Avatar
                          src={order.requester.profilePicture}
                          sx={{ width: 24, height: 24 }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: 12,
                          color: "#000000",
                          opacity: 0.6,
                        }}
                      >
                        Address
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 16,
                        color: "#000000",
                        opacity: 0.87,
                        textAlign: "left",
                        flex: 1,
                      }}
                    >
                      {formatAddress(order) || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 12,
                        color: "#000000",
                        opacity: 0.6,
                        minWidth: 80,
                      }}
                    >
                      Status
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flex: 1,
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box
                        sx={{ position: "relative", display: "inline-flex" }}
                      >
                        <Chip
                          label={order.orderStatus || "-"}
                          sx={{
                            backgroundColor: getOrderStatusColor(
                              order.orderStatus
                            ),
                            color: "white",
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            height: 20,
                            paddingRight:
                              order.orderStatus &&
                              (order.orderStatus === "Inspected" ||
                                order.orderStatus === "InspectorReturn")
                                ? "20px"
                                : undefined,
                          }}
                          size="small"
                        />
                        {order.orderStatus &&
                          (order.orderStatus === "Inspected" ||
                            order.orderStatus === "InspectorReturn") && (
                            <KeyboardArrowDownOutlinedIcon
                              fontSize="small"
                              sx={{
                                color: "white",
                                position: "absolute",
                                right: "4px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                              }}
                            />
                          )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No results found
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
